import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Building2, Wallet, FolderTree, Globe, Grid3X3, Users, 
  CheckCircle2, ChevronRight, ChevronLeft, Loader2, Sparkles,
  Plus, Trash2, Edit3, Save, X, Upload, Clock, Calendar,
  Percent, AlertCircle, Info, CreditCard, Banknote,
  Building, FileText, HardDrive, ShoppingBag, Settings,
  HelpCircle, Eye, EyeOff, Landmark, Receipt, ClipboardCheck
} from 'lucide-react';
import { OnboardingWelcome } from '../components/onboarding';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============ STEP INFO BOX - Explains each step ============
const StepInfoBox = ({ icon: Icon, title, description, children }) => (
  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-amber-600" />
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
        <p className="text-sm text-gray-600">{description}</p>
        {children}
      </div>
    </div>
  </div>
);

// ============ TOGGLE SWITCH ============
const Toggle = ({ enabled, onChange, color = 'amber' }) => {
  const colors = {
    amber: { bg: 'bg-amber-500', ring: 'ring-amber-300' },
    green: { bg: 'bg-green-500', ring: 'ring-green-300' },
    purple: { bg: 'bg-purple-500', ring: 'ring-purple-300' },
    blue: { bg: 'bg-blue-500', ring: 'ring-blue-300' }
  };
  const c = colors[color] || colors.amber;
  
  return (
    <button type="button" onClick={() => onChange(!enabled)}
      className={`w-12 h-6 rounded-full transition-colors ${enabled ? c.bg : 'bg-gray-300'}`}>
      <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${enabled ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`} />
    </button>
  );
};

// ============ STEP 1: RIAD INFO + CAPACITY ============
const StepRiadInfo = ({ data, onChange }) => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (data.logo) {
      // Handle both full URLs and relative paths
      const logoUrl = data.logo.startsWith('http') ? data.logo : `${API_URL}/uploads/${data.logo}`;
      setLogoPreview(logoUrl);
    }
  }, [data.logo]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const response = await api.post('/settings/riad/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Use the returned logo path, prepend API_URL for preview
      const logoPath = response.data.logo;
      const fullUrl = logoPath.startsWith('http') ? logoPath : `${API_URL}/uploads/${logoPath}`;
      setLogoPreview(fullUrl);
      onChange({ ...data, logo: logoPath });
      toast.success('Logo téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const capacity = data.capacity || { max_guests: 10, max_adults: 8, max_children: 4, beds: 5 };
  const updateCapacity = (field, value) => {
    onChange({ ...data, capacity: { ...capacity, [field]: value } });
  };

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={Building2} 
        title="Identité de votre établissement"
        description="Renseignez les informations de base de votre riad. Ces données apparaîtront sur vos documents officiels (factures, reçus). Vous pourrez les modifier plus tard."
      >
        <div className="mt-2 text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2">
          <strong>Astuce :</strong> Un logo professionnel renforce la confiance de vos clients
        </div>
      </StepInfoBox>

      {/* Logo + Name */}
      <div className="flex items-start gap-6">
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="w-28 h-28 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all overflow-hidden flex-shrink-0 bg-gray-50"
        >
          {uploading ? (
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          ) : logoPreview ? (
            <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-1" />
          ) : (
            <div className="text-center">
              <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
              <span className="text-xs text-gray-500">Logo</span>
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
        <div className="flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'établissement <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={data.name || ''}
              onChange={(e) => onChange({ ...data, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-lg font-medium"
              placeholder="Riad Al Andalus"
              data-testid="riad-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input type="email" value={data.email || ''} onChange={(e) => onChange({ ...data, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="contact@riad.ma" data-testid="riad-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input type="tel" value={data.phone || ''} onChange={(e) => onChange({ ...data, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="+212 5 24 XX XX XX" data-testid="riad-phone-input" />
            </div>
          </div>
        </div>
      </div>

      {/* Address & Location */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
          <input type="text" value={data.address || ''} onChange={(e) => onChange({ ...data, address: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="123 Derb Sidi Ahmed, Médina" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
          <input type="text" value={data.city || ''} onChange={(e) => onChange({ ...data, city: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="Marrakech" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
          <select value={data.country || 'MA'} onChange={(e) => onChange({ ...data, country: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg">
            <option value="MA">Maroc</option>
            <option value="FR">France</option>
            <option value="ES">Espagne</option>
            <option value="PT">Portugal</option>
            <option value="TN">Tunisie</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fuseau horaire</label>
          <select value={data.timezone || 'Africa/Casablanca'} onChange={(e) => onChange({ ...data, timezone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg">
            <option value="Africa/Casablanca">Casablanca (GMT+1)</option>
            <option value="Europe/Paris">Paris (GMT+1/+2)</option>
            <option value="Europe/London">Londres (GMT)</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Devise principale</label>
          <div className="flex gap-2">
            {[{ code: 'MAD', symbol: 'DH' }, { code: 'EUR', symbol: '€' }, { code: 'USD', symbol: '$' }].map(c => (
              <button key={c.code} type="button" onClick={() => onChange({ ...data, currency: c.code })}
                className={`flex-1 py-2 px-3 rounded-lg border-2 font-semibold transition-colors ${data.currency === c.code ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 hover:border-gray-300'}`}>
                {c.symbol} {c.code}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Capacity Section */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <h4 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Capacité du riad (location exclusive)
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          Votre riad est loué en exclusivité. Ces valeurs serviront de limites par défaut pour vos réservations.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max voyageurs (PAX)</label>
            <input type="number" min="1" max="50" value={capacity.max_guests || 10}
              onChange={(e) => updateCapacity('max_guests', parseInt(e.target.value) || 10)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-center text-lg font-semibold"
              data-testid="capacity-max-guests" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max adultes</label>
            <input type="number" min="1" max="50" value={capacity.max_adults || 8}
              onChange={(e) => updateCapacity('max_adults', parseInt(e.target.value) || 8)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-center" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max enfants</label>
            <input type="number" min="0" max="20" value={capacity.max_children || 4}
              onChange={(e) => updateCapacity('max_children', parseInt(e.target.value) || 4)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-center" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lits/Couchages</label>
            <input type="number" min="1" max="30" value={capacity.beds || 5}
              onChange={(e) => updateCapacity('beds', parseInt(e.target.value) || 5)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-center" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ STEP 2: FLUX FINANCIERS ============
const StepFluxFinanciers = ({ data, onChange }) => {
  const [editingFlux, setEditingFlux] = useState(null);
  
  // Flux avec leurs propriétés fiscales
  const fluxTypes = data.flux_types || [
    { code: 'CASH_CAISSE', label: 'Caisse espèces', color: '#22C55E', is_system: true, in_cash_balance: true, subject_to_vat: false, subject_to_is: false },
    { code: 'COMPTE_PERSO', label: 'Compte personnel', color: '#3B82F6', is_system: false, in_cash_balance: false, subject_to_vat: false, subject_to_is: false },
    { code: 'SOCIETE', label: 'Compte société', color: '#8B5CF6', is_system: false, in_cash_balance: false, subject_to_vat: true, subject_to_is: true }
  ];
  
  const paymentMethods = data.payment_methods || [
    { code: 'CASH', label: 'Espèces', icon: '💵', is_active: true, default_flux: 'CASH_CAISSE' },
    { code: 'CARD', label: 'Carte bancaire', icon: '💳', is_active: true, default_flux: 'SOCIETE' },
    { code: 'TRANSFER', label: 'Virement', icon: '🏦', is_active: true, default_flux: 'SOCIETE' },
    { code: 'OTA', label: 'Via plateforme OTA', icon: '🌐', is_active: true, default_flux: 'SOCIETE' },
    { code: 'CHECK', label: 'Chèque', icon: '📝', is_active: false, default_flux: 'SOCIETE' }
  ];

  const [newFlux, setNewFlux] = useState({ 
    code: '', label: '', color: '#6B7280', 
    in_cash_balance: false, subject_to_vat: false, subject_to_is: false 
  });

  const togglePayment = (code) => {
    const updated = paymentMethods.map(p => p.code === code ? { ...p, is_active: !p.is_active } : p);
    onChange({ ...data, payment_methods: updated });
  };

  const updatePaymentFlux = (code, fluxCode) => {
    const updated = paymentMethods.map(p => p.code === code ? { ...p, default_flux: fluxCode } : p);
    onChange({ ...data, payment_methods: updated });
  };

  const addFlux = () => {
    if (!newFlux.label.trim()) return;
    const code = newFlux.code || newFlux.label.toUpperCase().replace(/\s+/g, '_');
    if (fluxTypes.find(f => f.code === code)) {
      toast.error('Ce code existe déjà');
      return;
    }
    const updated = [...fluxTypes, { 
      code, 
      label: newFlux.label, 
      color: newFlux.color, 
      is_system: false,
      in_cash_balance: newFlux.in_cash_balance,
      subject_to_vat: newFlux.subject_to_vat,
      subject_to_is: newFlux.subject_to_is
    }];
    onChange({ ...data, flux_types: updated });
    setNewFlux({ code: '', label: '', color: '#6B7280', in_cash_balance: false, subject_to_vat: false, subject_to_is: false });
  };

  const removeFlux = (code) => {
    const flux = fluxTypes.find(f => f.code === code);
    if (flux?.is_system) {
      toast.error('Impossible de supprimer un flux système');
      return;
    }
    const updated = fluxTypes.filter(f => f.code !== code);
    onChange({ ...data, flux_types: updated });
  };

  const updateFluxProperty = (code, property, value) => {
    const updated = fluxTypes.map(f => f.code === code ? { ...f, [property]: value } : f);
    onChange({ ...data, flux_types: updated });
  };

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={Landmark} 
        title="Où encaissez-vous vos paiements ?"
        description="Définissez vos comptes de trésorerie : caisse espèces, compte bancaire, etc. Cela vous permettra de suivre précisément vos recettes et dépenses par compte."
      >
        <div className="mt-2 text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2">
          <strong>Par défaut :</strong> Une caisse espèces et un compte bancaire sont déjà configurés. Vous pouvez les garder ou les personnaliser.
        </div>
      </StepInfoBox>

      {/* Flux Types avec configuration fiscale */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Landmark className="w-5 h-5 text-purple-500" />
          Flux de trésorerie
        </h4>
        
        <div className="space-y-3 mb-4">
          {fluxTypes.map(flux => (
            <div key={flux.code} className="p-4 rounded-xl border-2 transition-all" 
              style={{ borderColor: flux.color + '40', backgroundColor: flux.color + '08' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: flux.color }} />
                  <span className="font-medium text-gray-900">{flux.label}</span>
                  {flux.is_system && <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">Système</span>}
                </div>
                {!flux.is_system && (
                  <button type="button" onClick={() => removeFlux(flux.code)} 
                    className="text-gray-400 hover:text-red-500 p-1">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Configuration fiscale du flux */}
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={flux.in_cash_balance || false}
                    onChange={(e) => updateFluxProperty(flux.code, 'in_cash_balance', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                  <span className="text-sm text-gray-700">Solde caisse</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={flux.subject_to_vat || false}
                    onChange={(e) => updateFluxProperty(flux.code, 'subject_to_vat', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">Soumis TVA</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={flux.subject_to_is || false}
                    onChange={(e) => updateFluxProperty(flux.code, 'subject_to_is', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                  <span className="text-sm text-gray-700">Soumis IS</span>
                </label>
              </div>
            </div>
          ))}
        </div>
        
        {/* Ajout nouveau flux */}
        <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <p className="text-sm font-medium text-gray-700 mb-3">Ajouter un flux</p>
          <div className="flex gap-2 items-end mb-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Libellé</label>
              <input type="text" value={newFlux.label} onChange={(e) => setNewFlux({ ...newFlux, label: e.target.value })}
                placeholder="Ex: Compte épargne" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Couleur</label>
              <input type="color" value={newFlux.color} onChange={(e) => setNewFlux({ ...newFlux, color: e.target.value })}
                className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
            </div>
          </div>
          <div className="flex items-center gap-4 mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={newFlux.in_cash_balance}
                onChange={(e) => setNewFlux({ ...newFlux, in_cash_balance: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-green-600" />
              <span className="text-sm text-gray-600">Inclus solde caisse</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={newFlux.subject_to_vat}
                onChange={(e) => setNewFlux({ ...newFlux, subject_to_vat: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-600">Soumis TVA</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={newFlux.subject_to_is}
                onChange={(e) => setNewFlux({ ...newFlux, subject_to_is: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-purple-600" />
              <span className="text-sm text-gray-600">Soumis IS</span>
            </label>
          </div>
          <button type="button" onClick={addFlux} disabled={!newFlux.label.trim()}
            className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Ajouter ce flux
          </button>
        </div>
      </div>

      {/* Payment Methods with Flux Assignment */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-amber-500" />
          Moyens de paiement
        </h4>
        <div className="space-y-3">
          {paymentMethods.map(pm => (
            <div key={pm.code} className={`p-4 rounded-xl border-2 transition-all ${pm.is_active ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-4">
                <button type="button" onClick={() => togglePayment(pm.code)} className="flex-shrink-0">
                  <span className="text-2xl">{pm.icon}</span>
                </button>
                <div className="flex-1">
                  <p className={`font-medium ${pm.is_active ? 'text-gray-900' : 'text-gray-400'}`}>{pm.label}</p>
                </div>
                {pm.is_active && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Flux:</span>
                    <select value={pm.default_flux || ''} onChange={(e) => updatePaymentFlux(pm.code, e.target.value)}
                      className="px-2 py-1 border border-gray-200 rounded text-sm">
                      {fluxTypes.map(f => (
                        <option key={f.code} value={f.code}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                <Toggle enabled={pm.is_active} onChange={() => togglePayment(pm.code)} color="green" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ STEP 3: TAXES ============
const StepTaxes = ({ data, onChange }) => {
  const touristTax = data.tourist_tax || { 
    enabled: true, amount: 25, currency: 'MAD', 
    per_night: true, per_person: true, min_age: 12, max_nights: 0, exempt_children: true 
  };
  const taxes = data.taxes || { 
    vat: { rate: 10, enabled: false, included_in_price: true },
    corporate_tax: { rate: 30, enabled: false }
  };

  const updateTouristTax = (field, value) => {
    onChange({ ...data, tourist_tax: { ...touristTax, [field]: value } });
  };

  const updateVAT = (field, value) => {
    onChange({ ...data, taxes: { ...taxes, vat: { ...taxes.vat, [field]: value } } });
  };

  const updateCorporateTax = (field, value) => {
    onChange({ ...data, taxes: { ...taxes, corporate_tax: { ...taxes.corporate_tax, [field]: value } } });
  };

  // Calculate example
  const exampleNights = 3;
  const exampleGuests = 2;
  const touristTaxTotal = touristTax.amount * (touristTax.per_night ? exampleNights : 1) * (touristTax.per_person ? exampleGuests : 1);

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={Receipt} 
        title="Taxes et fiscalité"
        description="Configurez les taxes applicables à votre établissement. Nous avons pré-rempli les valeurs standard pour le Maroc. Vous pouvez les ajuster selon votre situation."
      >
        <div className="mt-2 text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2">
          <strong>Valeurs par défaut :</strong> Taxe de séjour 25 DHS/nuit/personne (standard hôtellerie Maroc)
        </div>
      </StepInfoBox>

      {/* Taxe de séjour */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-500" />
              Taxe de séjour
            </h4>
            <p className="text-sm text-gray-500">Taxe collectée pour la commune (obligatoire au Maroc)</p>
          </div>
          <Toggle enabled={touristTax.enabled} onChange={(v) => updateTouristTax('enabled', v)} />
        </div>

        {touristTax.enabled && (
          <div className="space-y-4 pt-4 border-t">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant</label>
                <div className="flex">
                  <input type="number" min="0" step="1" value={touristTax.amount}
                    onChange={(e) => updateTouristTax('amount', parseFloat(e.target.value) || 0)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg text-center font-semibold"
                    data-testid="tourist-tax-amount" />
                  <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-200 rounded-r-lg text-sm font-medium">DHS</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Âge minimum</label>
                <div className="flex">
                  <input type="number" min="0" max="18" value={touristTax.min_age}
                    onChange={(e) => updateTouristTax('min_age', parseInt(e.target.value) || 0)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg text-center" />
                  <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-200 rounded-r-lg text-sm">ans</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max nuits taxables</label>
                <div className="flex">
                  <input type="number" min="0" value={touristTax.max_nights}
                    onChange={(e) => updateTouristTax('max_nights', parseInt(e.target.value) || 0)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg text-center" />
                  <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-200 rounded-r-lg text-xs whitespace-nowrap">0=illimité</span>
                </div>
              </div>
              <div className="flex flex-col justify-end gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={touristTax.per_night} onChange={(e) => updateTouristTax('per_night', e.target.checked)}
                    className="w-4 h-4 rounded text-amber-600" />
                  <span className="text-sm">Par nuit</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={touristTax.per_person} onChange={(e) => updateTouristTax('per_person', e.target.checked)}
                    className="w-4 h-4 rounded text-amber-600" />
                  <span className="text-sm">Par personne</span>
                </label>
              </div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Exemple:</strong> {exampleGuests} adultes × {exampleNights} nuits = <span className="font-bold text-lg">{touristTaxTotal} DHS</span>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* TVA */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Percent className="w-5 h-5 text-purple-500" />
              TVA (Taxe sur la Valeur Ajoutée)
            </h4>
            <p className="text-sm text-gray-500">Applicable sur vos ventes et services hôteliers</p>
          </div>
          <Toggle enabled={taxes.vat?.enabled} onChange={(v) => updateVAT('enabled', v)} color="purple" />
        </div>

        {taxes.vat?.enabled && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Taux TVA</label>
              <div className="flex">
                <input type="number" min="0" max="100" step="0.5" value={taxes.vat?.rate || 10}
                  onChange={(e) => updateVAT('rate', parseFloat(e.target.value) || 0)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg text-center font-semibold"
                  data-testid="vat-rate" />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-200 rounded-r-lg">%</span>
              </div>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={taxes.vat?.included_in_price !== false} 
                  onChange={(e) => updateVAT('included_in_price', e.target.checked)}
                  className="w-4 h-4 rounded text-purple-600" />
                <span className="text-sm">TVA incluse dans les prix</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Impôt sur les sociétés */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-500" />
              Impôt sur les sociétés (IS)
            </h4>
            <p className="text-sm text-gray-500">Pour le calcul prévisionnel de vos charges fiscales</p>
          </div>
          <Toggle enabled={taxes.corporate_tax?.enabled} onChange={(v) => updateCorporateTax('enabled', v)} color="blue" />
        </div>

        {taxes.corporate_tax?.enabled && (
          <div className="pt-4 border-t">
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">Taux IS</label>
              <div className="flex">
                <input type="number" min="0" max="100" step="0.5" value={taxes.corporate_tax?.rate || 30}
                  onChange={(e) => updateCorporateTax('rate', parseFloat(e.target.value) || 0)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg text-center font-semibold" />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-200 rounded-r-lg">%</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Taux standard au Maroc: 30%</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ STEP 4: PRODUCTS/SERVICES ============
const StepProducts = ({ data, onChange }) => {
  const products = data.products || [];
  const suggestions = [
    { name: 'Petit-déjeuner', price: 150, icon: '🥐' },
    { name: 'Déjeuner', price: 250, icon: '🍽️' },
    { name: 'Dîner', price: 350, icon: '🍷' },
    { name: 'Transfert Aéroport', price: 300, icon: '🚗' },
    { name: 'Excursion Journée', price: 800, icon: '🏜️' },
    { name: 'Massage 1h', price: 400, icon: '💆' },
    { name: 'Hammam', price: 200, icon: '🧖' },
    { name: 'Cours de cuisine', price: 500, icon: '👨‍🍳' },
    { name: 'Late check-out', price: 500, icon: '🕐' }
  ];
  const [newProduct, setNewProduct] = useState({ name: '', price: 0 });
  const [editingProduct, setEditingProduct] = useState(null);

  const addProduct = (product) => {
    if (products.find(p => p.name === product.name)) {
      toast.error('Ce service existe déjà');
      return;
    }
    onChange({ ...data, products: [...products, { ...product, is_active: true }] });
  };

  const removeProduct = (name) => {
    onChange({ ...data, products: products.filter(p => p.name !== name) });
  };

  const updateProduct = (oldName, updatedProduct) => {
    const updated = products.map(p => p.name === oldName ? { ...p, ...updatedProduct } : p);
    onChange({ ...data, products: updated });
    setEditingProduct(null);
  };

  const addCustomProduct = () => {
    if (!newProduct.name.trim()) return;
    addProduct(newProduct);
    setNewProduct({ name: '', price: 0 });
  };

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={ShoppingBag} 
        title="Services et produits à vendre"
        description="Ajoutez les services que vous proposez à vos clients : petit-déjeuner, transfert aéroport, excursions, etc. Vous pourrez les facturer rapidement depuis l'application."
      >
        <div className="mt-2 text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2">
          <strong>Suggestion :</strong> Commencez par le petit-déjeuner et ajoutez d'autres services plus tard
        </div>
      </StepInfoBox>

      {/* Selected Products */}
      {products.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h5 className="font-medium text-gray-900 mb-3">Services configurés ({products.length})</h5>
          <div className="space-y-2">
            {products.map(p => (
              <div key={p.name} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                {editingProduct === p.name ? (
                  <>
                    <input type="text" value={p.name} onChange={(e) => updateProduct(p.name, { name: e.target.value })}
                      className="flex-1 px-2 py-1 border rounded" />
                    <input type="number" value={p.price} onChange={(e) => updateProduct(editingProduct, { price: parseFloat(e.target.value) || 0 })}
                      className="w-20 px-2 py-1 border rounded text-center" />
                    <span className="text-sm text-gray-500">DHS</span>
                    <button type="button" onClick={() => setEditingProduct(null)} className="text-green-600"><Save className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 font-medium">{p.name}</span>
                    <span className="text-green-700 font-semibold">{p.price} DHS</span>
                    <button type="button" onClick={() => setEditingProduct(p.name)} className="text-gray-400 hover:text-blue-500"><Edit3 className="w-4 h-4" /></button>
                    <button type="button" onClick={() => removeProduct(p.name)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      <div>
        <h5 className="font-medium text-gray-900 mb-3">Services suggérés</h5>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {suggestions.filter(s => !products.find(p => p.name === s.name)).map(s => (
            <button key={s.name} type="button" onClick={() => addProduct(s)}
              className="p-3 border border-gray-200 rounded-xl text-left hover:border-amber-500 hover:bg-amber-50 transition-all group">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{s.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{s.name}</p>
                  <p className="text-sm text-amber-600 font-semibold">{s.price} DHS</p>
                </div>
                <Plus className="w-5 h-5 text-gray-400 group-hover:text-amber-500" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Product */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h5 className="font-medium text-gray-900 mb-3">Ajouter un service personnalisé</h5>
        <div className="flex gap-3">
          <input type="text" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
            placeholder="Nom du service" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg" />
          <div className="flex">
            <input type="number" value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: parseFloat(e.target.value) || 0 })}
              placeholder="Prix" className="w-24 px-3 py-2 border border-gray-200 rounded-l-lg text-center" />
            <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-200 rounded-r-lg text-sm font-medium">DHS</span>
          </div>
          <button type="button" onClick={addCustomProduct} disabled={!newProduct.name.trim()}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ STEP 5: CATEGORIES ============
const StepCategories = ({ data, onChange }) => {
  const categories = data.categories || [];
  const suggestions = {
    PRODUCT: ['Services', 'Restaurant', 'Bar', 'Boutique', 'Spa', 'Activités'],
    EXPENSE: ['Maintenance', 'Fournisseurs', 'Salaires', 'Marketing', 'Transport'],
    CHARGE: ['Électricité', 'Eau', 'Internet', 'Loyer', 'Assurance', 'Impôts']
  };

  const types = [
    { code: 'PRODUCT', label: 'Catégories de produits', color: 'blue', icon: '🛒', description: 'Pour classer vos services et produits vendus' },
    { code: 'EXPENSE', label: 'Catégories de dépenses', color: 'red', icon: '💸', description: 'Pour suivre vos dépenses opérationnelles' },
    { code: 'CHARGE', label: 'Catégories de charges', color: 'purple', icon: '📊', description: 'Pour les charges fixes et récurrentes' }
  ];

  const [newCategories, setNewCategories] = useState({ PRODUCT: '', EXPENSE: '', CHARGE: '' });

  const addCategory = (name, type) => {
    if (!name.trim()) return;
    if (categories.find(c => c.name.toLowerCase() === name.toLowerCase() && c.type === type)) {
      toast.error('Cette catégorie existe déjà');
      return;
    }
    onChange({ ...data, categories: [...categories, { name, type }] });
  };

  const removeCategory = (name, type) => {
    onChange({ ...data, categories: categories.filter(c => !(c.name === name && c.type === type)) });
  };

  const addCustomCategory = (type) => {
    const name = newCategories[type];
    if (!name.trim()) return;
    addCategory(name, type);
    setNewCategories({ ...newCategories, [type]: '' });
  };

  const colorClasses = {
    blue: { border: 'border-blue-200', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700', tag: 'border-blue-200 hover:border-blue-400', chip: 'bg-blue-100' },
    red: { border: 'border-red-200', bg: 'bg-red-50', badge: 'bg-red-100 text-red-700', tag: 'border-red-200 hover:border-red-400', chip: 'bg-red-100' },
    purple: { border: 'border-purple-200', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700', tag: 'border-purple-200 hover:border-purple-400', chip: 'bg-purple-100' }
  };

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={FolderTree} 
        title="Catégories de dépenses et revenus"
        description="Classez vos transactions par catégorie pour mieux suivre votre activité. Nous avons préparé des suggestions adaptées à l'hôtellerie."
      >
        <div className="mt-2 text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2">
          <strong>Par défaut :</strong> Les catégories essentielles sont déjà configurées. Cliquez sur une suggestion pour l'ajouter.
        </div>
      </StepInfoBox>

      {types.map(type => {
        const typeCats = categories.filter(c => c.type === type.code);
        const availableSuggestions = (suggestions[type.code] || []).filter(s => !typeCats.find(c => c.name === s));
        const colors = colorClasses[type.color];
        
        return (
          <div key={type.code} className={`border rounded-xl p-4 ${colors.bg} ${colors.border}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{type.icon}</span>
              <h5 className="font-semibold text-gray-900">{type.label}</h5>
              <span className={`ml-auto px-2 py-0.5 text-xs rounded-full ${colors.badge}`}>
                {typeCats.length} catégories
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-3">{type.description}</p>
            
            {/* Selected Categories */}
            {typeCats.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {typeCats.map(c => (
                  <span key={c.name} className={`inline-flex items-center gap-1 px-3 py-1.5 bg-white rounded-lg text-sm border ${colors.border}`}>
                    {c.name}
                    <button type="button" onClick={() => removeCategory(c.name, type.code)} className="hover:text-red-500 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            {/* Suggestions */}
            {availableSuggestions.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {availableSuggestions.map(s => (
                  <button key={s} type="button" onClick={() => addCategory(s, type.code)}
                    className={`px-3 py-1 border border-dashed rounded-lg text-sm ${colors.tag} transition-colors flex items-center gap-1 bg-white/50`}>
                    <Plus className="w-3 h-3" /> {s}
                  </button>
                ))}
              </div>
            )}

            {/* Custom Category Input */}
            <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200/50">
              <input 
                type="text" 
                value={newCategories[type.code]} 
                onChange={(e) => setNewCategories({ ...newCategories, [type.code]: e.target.value })}
                placeholder="Nouvelle catégorie personnalisée..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                onKeyPress={(e) => e.key === 'Enter' && addCustomCategory(type.code)}
              />
              <button type="button" onClick={() => addCustomCategory(type.code)} 
                disabled={!newCategories[type.code].trim()}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============ STEP 6: CHANNELS (OTA + iCal) ============
const StepChannels = ({ data, onChange }) => {
  const defaultChannels = [
    { code: 'DIRECT', label: 'Direct', color: '#22C55E', commission_rate: 0, is_ota: false, payout_delay_days: 0, default_flux: 'COMPTE_PERSO', is_active: true, is_system: true },
    { code: 'BOOKING', label: 'Booking.com', color: '#003580', commission_rate: 15, is_ota: true, payout_delay_days: 7, default_flux: 'SOCIETE', is_active: true },
    { code: 'AIRBNB', label: 'Airbnb', color: '#FF5A5F', commission_rate: 15, is_ota: true, payout_delay_days: 3, default_flux: 'SOCIETE', is_active: true },
    { code: 'EXPEDIA', label: 'Expedia', color: '#FBCC12', commission_rate: 18, is_ota: true, payout_delay_days: 14, default_flux: 'SOCIETE', is_active: false },
    { code: 'VRBO', label: 'VRBO/Abritel', color: '#3D5A80', commission_rate: 8, is_ota: true, payout_delay_days: 7, default_flux: 'SOCIETE', is_active: false }
  ];
  
  const fluxOptions = [
    { value: 'CASH_CAISSE', label: 'Caisse espèces' },
    { value: 'COMPTE_PERSO', label: 'Compte personnel' },
    { value: 'SOCIETE', label: 'Compte société' },
    { value: 'VIREMENT_OTA', label: 'Virement OTA' }
  ];
  
  const channels = data.channels || defaultChannels;
  const [newChannel, setNewChannel] = useState({ code: '', label: '', color: '#6B7280', commission_rate: 15, payout_delay_days: 7, default_flux: 'SOCIETE' });
  const [editingChannel, setEditingChannel] = useState(null);

  const toggleChannel = (code) => {
    const ch = channels.find(c => c.code === code);
    if (ch?.is_system) return;
    const updated = channels.map(c => c.code === code ? { ...c, is_active: !c.is_active } : c);
    onChange({ ...data, channels: updated });
  };

  const updateChannel = (code, field, value) => {
    const updated = channels.map(c => c.code === code ? { ...c, [field]: value } : c);
    onChange({ ...data, channels: updated });
  };

  const addChannel = () => {
    if (!newChannel.label.trim()) return;
    const code = newChannel.code || newChannel.label.toUpperCase().replace(/\s+/g, '_');
    if (channels.find(c => c.code === code)) {
      toast.error('Ce canal existe déjà');
      return;
    }
    const updated = [...channels, { ...newChannel, code, is_ota: true, is_active: true }];
    onChange({ ...data, channels: updated });
    setNewChannel({ code: '', label: '', color: '#6B7280', commission_rate: 15, payout_delay_days: 7 });
  };

  const removeChannel = (code) => {
    const ch = channels.find(c => c.code === code);
    if (ch?.is_system) {
      toast.error('Impossible de supprimer un canal système');
      return;
    }
    const updated = channels.filter(c => c.code !== code);
    onChange({ ...data, channels: updated });
  };

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={Globe} 
        title="D'où viennent vos réservations ?"
        description="Indiquez vos sources de réservation : Booking.com, Airbnb, téléphone, etc. Cela vous permettra de suivre quelle plateforme vous rapporte le plus."
      >
        <div className="mt-2 text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2">
          <strong>Astuce :</strong> Les commissions Booking/Airbnb sont pré-remplies. Ajustez-les selon vos contrats réels.
        </div>
      </StepInfoBox>

      <div className="space-y-3">
        {channels.map(ch => (
          <div key={ch.code} className={`border-2 rounded-xl overflow-hidden transition-all ${ch.is_active ? 'border-amber-400' : 'border-gray-200 opacity-60'}`}>
            <div className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: ch.color + '20' }}>
                <Globe className="w-5 h-5" style={{ color: ch.color }} />
              </div>
              <div className="flex-1 min-w-0">
                {editingChannel === ch.code ? (
                  <input type="text" value={ch.label} onChange={(e) => updateChannel(ch.code, 'label', e.target.value)}
                    className="px-2 py-1 border rounded w-full" autoFocus />
                ) : (
                  <p className={`font-semibold truncate ${ch.is_active ? 'text-gray-900' : 'text-gray-400'}`}>{ch.label}</p>
                )}
                <p className="text-xs text-gray-500">{ch.is_ota ? `Commission ${ch.commission_rate}% • Délai ${ch.payout_delay_days}j` : 'Sans commission • Paiement direct'}</p>
              </div>
              
              <div className="flex items-center gap-2">
                {!ch.is_system && (
                  <>
                    <button type="button" onClick={() => setEditingChannel(editingChannel === ch.code ? null : ch.code)}
                      className="p-2 text-gray-400 hover:text-blue-500">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => removeChannel(ch.code)}
                      className="p-2 text-gray-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                {ch.is_system ? (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Requis</span>
                ) : (
                  <Toggle enabled={ch.is_active} onChange={() => toggleChannel(ch.code)} />
                )}
              </div>
            </div>
            
            {ch.is_active && ch.is_ota && (
              <div className="px-4 pb-4 pt-2 border-t border-gray-100 grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Commission (%)</label>
                  <input type="number" min="0" max="50" step="0.5" value={ch.commission_rate}
                    onChange={(e) => updateChannel(ch.code, 'commission_rate', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-center" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Délai virement (jours)</label>
                  <input type="number" min="0" max="60" value={ch.payout_delay_days}
                    onChange={(e) => updateChannel(ch.code, 'payout_delay_days', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-center" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Flux de paiement</label>
                  <select value={ch.default_flux || 'SOCIETE'}
                    onChange={(e) => updateChannel(ch.code, 'default_flux', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg">
                    {fluxOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add New Channel */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h5 className="font-medium text-gray-900 mb-3">Ajouter un canal personnalisé</h5>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <input type="text" value={newChannel.label} onChange={(e) => setNewChannel({ ...newChannel, label: e.target.value })}
            placeholder="Nom du canal" className="px-3 py-2 border border-gray-200 rounded-lg" />
          <div className="flex gap-2">
            <input type="number" value={newChannel.commission_rate} onChange={(e) => setNewChannel({ ...newChannel, commission_rate: parseFloat(e.target.value) || 0 })}
              placeholder="%" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-center" />
            <span className="px-2 py-2 text-gray-400">%</span>
          </div>
          <input type="number" value={newChannel.payout_delay_days} onChange={(e) => setNewChannel({ ...newChannel, payout_delay_days: parseInt(e.target.value) || 0 })}
            placeholder="Délai (j)" className="px-3 py-2 border border-gray-200 rounded-lg text-center" />
          <select value={newChannel.default_flux} onChange={(e) => setNewChannel({ ...newChannel, default_flux: e.target.value })}
            className="px-3 py-2 border border-gray-200 rounded-lg">
            {fluxOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button type="button" onClick={addChannel} disabled={!newChannel.label.trim()}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2">
            <Plus className="w-5 h-5" /> Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ STEP 7: PRECHECKIN CONFIG ============
const StepPrecheckin = ({ data, onChange }) => {
  const config = data.precheckin_config || {
    enabled: true,
    require_document: true,
    require_signature: false,
    auto_send_link: false,
    send_days_before: 3,
    check_in_time: '15:00',
    check_out_time: '11:00'
  };
  
  const rules = data.precheckin_rules || [
    'Non-fumeur dans les chambres et espaces communs',
    'Respect du calme entre 22h et 8h',
    'Animaux de compagnie non admis',
    'Pièce d\'identité obligatoire à l\'arrivée',
    'Paiement du solde à l\'arrivée'
  ];
  
  const extras = data.precheckin_extras || [
    'Petit-déjeuner inclus (8h00 - 10h00)',
    'Transfert aéroport sur demande',
    'Excursions et activités locales',
    'Spa et hammam (sur réservation)',
    'Dîner traditionnel marocain'
  ];

  const [newRule, setNewRule] = useState('');
  const [newExtra, setNewExtra] = useState('');

  const updateConfig = (field, value) => {
    onChange({ ...data, precheckin_config: { ...config, [field]: value } });
  };

  const addRule = () => {
    if (!newRule.trim()) return;
    onChange({ ...data, precheckin_rules: [...rules, newRule] });
    setNewRule('');
  };

  const removeRule = (index) => {
    const updated = rules.filter((_, i) => i !== index);
    onChange({ ...data, precheckin_rules: updated });
  };

  const addExtra = () => {
    if (!newExtra.trim()) return;
    onChange({ ...data, precheckin_extras: [...extras, newExtra] });
    setNewExtra('');
  };

  const removeExtra = (index) => {
    const updated = extras.filter((_, i) => i !== index);
    onChange({ ...data, precheckin_extras: updated });
  };

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={ClipboardCheck} 
        title="Pré-check-in en ligne"
        description="Gagnez du temps à l'arrivée ! Vos clients remplissent leurs infos (identité, passeport) avant d'arriver. Vous pouvez aussi afficher vos règles de maison et services optionnels."
      >
        <div className="mt-2 text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2">
          <strong>Avantage :</strong> Fiche de police pré-remplie, accueil personnalisé, upselling automatique
        </div>
      </StepInfoBox>

      {/* Main Config */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h5 className="font-semibold text-gray-900">Activer le pré-check-in</h5>
            <p className="text-sm text-gray-500">Envoyer un lien aux clients pour renseigner leurs informations</p>
          </div>
          <Toggle enabled={config.enabled} onChange={(v) => updateConfig('enabled', v)} color="green" />
        </div>

        {config.enabled && (
          <>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure d'arrivée affichée</label>
                <input type="time" value={config.check_in_time} onChange={(e) => updateConfig('check_in_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure de départ affichée</label>
                <input type="time" value={config.check_out_time} onChange={(e) => updateConfig('check_out_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4 pt-4 border-t">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.require_document} onChange={(e) => updateConfig('require_document', e.target.checked)}
                  className="w-4 h-4 rounded text-amber-600" />
                <span className="text-sm">Exiger un document d'identité</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.require_signature} onChange={(e) => updateConfig('require_signature', e.target.checked)}
                  className="w-4 h-4 rounded text-amber-600" />
                <span className="text-sm">Exiger une signature</span>
              </label>
            </div>
          </>
        )}
      </div>

      {/* Rules */}
      {config.enabled && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-500" />
            Règlement intérieur
          </h5>
          <p className="text-xs text-gray-500 mb-3">Ces règles seront affichées sur le formulaire de pré-check-in</p>
          
          <div className="space-y-2 mb-4">
            {rules.map((rule, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <span className="flex-1 text-sm">{rule}</span>
                <button type="button" onClick={() => removeRule(i)} className="text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          
          <div className="flex gap-2">
            <input type="text" value={newRule} onChange={(e) => setNewRule(e.target.value)}
              placeholder="Ajouter une règle..." className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              onKeyPress={(e) => e.key === 'Enter' && addRule()} />
            <button type="button" onClick={addRule} disabled={!newRule.trim()}
              className="px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Extras */}
      {config.enabled && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            Services et extras
          </h5>
          <p className="text-xs text-gray-500 mb-3">Informez vos clients des services disponibles</p>
          
          <div className="space-y-2 mb-4">
            {extras.map((extra, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
                <span className="flex-1 text-sm">{extra}</span>
                <button type="button" onClick={() => removeExtra(i)} className="text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          
          <div className="flex gap-2">
            <input type="text" value={newExtra} onChange={(e) => setNewExtra(e.target.value)}
              placeholder="Ajouter un service..." className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              onKeyPress={(e) => e.key === 'Enter' && addExtra()} />
            <button type="button" onClick={addExtra} disabled={!newExtra.trim()}
              className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ STEP 8: CALENDAR SETTINGS ============
const StepCalendar = ({ data, onChange }) => {
  const config = {
    check_in_time: data.check_in_time || '15:00',
    check_out_time: data.check_out_time || '11:00',
    min_nights: data.min_nights || 1,
    max_nights: data.max_nights || 30,
    advance_booking_days: data.advance_booking_days || 365,
    same_day_booking: data.same_day_booking !== false
  };

  const update = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={Calendar} 
        title="Paramètres de réservation"
        description="Définissez les horaires par défaut d'arrivée et de départ, ainsi que les contraintes de durée de séjour. Ces valeurs seront utilisées par défaut pour chaque nouvelle réservation."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h5 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-green-500" />
            Horaires par défaut
          </h5>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Check-in</label>
              <input type="time" value={config.check_in_time} onChange={(e) => update('check_in_time', e.target.value)}
                className="w-full px-3 py-3 border border-gray-200 rounded-lg text-center text-lg font-semibold"
                data-testid="check-in-time" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Check-out</label>
              <input type="time" value={config.check_out_time} onChange={(e) => update('check_out_time', e.target.value)}
                className="w-full px-3 py-3 border border-gray-200 rounded-lg text-center text-lg font-semibold"
                data-testid="check-out-time" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h5 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-500" />
            Durée de séjour
          </h5>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Min nuits</label>
              <input type="number" min="1" max="30" value={config.min_nights}
                onChange={(e) => update('min_nights', parseInt(e.target.value) || 1)}
                className="w-full px-3 py-3 border border-gray-200 rounded-lg text-center text-lg" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max nuits</label>
              <input type="number" min="1" max="365" value={config.max_nights}
                onChange={(e) => update('max_nights', parseInt(e.target.value) || 30)}
                className="w-full px-3 py-3 border border-gray-200 rounded-lg text-center text-lg" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <h5 className="font-semibold text-gray-900">Réservation même jour</h5>
            <p className="text-sm text-gray-500">Autoriser les réservations pour aujourd'hui</p>
          </div>
          <Toggle enabled={config.same_day_booking} onChange={(v) => update('same_day_booking', v)} color="green" />
        </div>
      </div>
    </div>
  );
};

// ============ STEP 9: DOCUMENTS CONFIG ============
const StepDocuments = ({ data, onChange }) => {
  const config = {
    business_name: data.business_name || '',
    legal_name: data.legal_name || '',
    tax_id: data.tax_id || '',
    invoice_prefix: data.invoice_prefix || 'FAC-',
    receipt_prefix: data.receipt_prefix || 'REC-',
    show_logo_on_documents: data.show_logo_on_documents !== false,
    police_form_enabled: data.police_form_enabled !== false
  };

  const update = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={FileText} 
        title="Documents et facturation"
        description="Configurez les informations légales qui apparaîtront sur vos documents : factures, reçus, fiches de police. Ces informations sont essentielles pour la conformité fiscale."
      />

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h5 className="font-semibold text-gray-900 mb-4">Informations légales</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Nom commercial</label>
            <input type="text" value={config.business_name} onChange={(e) => update('business_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="Riad Al Andalus" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Raison sociale</label>
            <input type="text" value={config.legal_name} onChange={(e) => update('legal_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="SARL Riad Al Andalus" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Identifiant fiscal (ICE/IF/RC)</label>
            <input type="text" value={config.tax_id} onChange={(e) => update('tax_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="001234567890123" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h5 className="font-semibold text-gray-900 mb-4">Numérotation des documents</h5>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Préfixe factures</label>
            <input type="text" value={config.invoice_prefix} onChange={(e) => update('invoice_prefix', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
            <p className="text-xs text-gray-400 mt-1">Ex: FAC-2024-001</p>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Préfixe reçus</label>
            <input type="text" value={config.receipt_prefix} onChange={(e) => update('receipt_prefix', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
          <div>
            <p className="font-medium">Afficher le logo sur les documents</p>
            <p className="text-sm text-gray-500">Le logo apparaîtra sur les factures et reçus</p>
          </div>
          <Toggle enabled={config.show_logo_on_documents} onChange={(v) => update('show_logo_on_documents', v)} color="green" />
        </div>
        <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
          <div>
            <p className="font-medium">Fiche de police</p>
            <p className="text-sm text-gray-500">Activer le formulaire de déclaration pour la police</p>
          </div>
          <Toggle enabled={config.police_form_enabled} onChange={(v) => update('police_form_enabled', v)} color="green" />
        </div>
      </div>
    </div>
  );
};

// ============ STEP 10: USERS ============
const StepUsers = ({ data, onChange }) => {
  const existingUsers = data.users || [];
  const [newUsers, setNewUsers] = useState(data.new_users || []);
  
  // Utiliser les rôles dynamiques chargés depuis le backend, avec fallback
  const roles = data.available_roles?.length > 0 
    ? data.available_roles 
    : [
        { code: 'OWNER', label: 'Propriétaire', description: 'Accès complet' },
        { code: 'MANAGER', label: 'Gouvernante', description: 'Gestion quotidienne' },
        { code: 'STAFF', label: 'Staff', description: 'Accès limité' }
      ];

  const addUser = () => {
    const updated = [...newUsers, { email: '', first_name: '', last_name: '', role: 'STAFF', temp_password: '' }];
    setNewUsers(updated);
    onChange({ ...data, new_users: updated });
  };

  const updateUser = (index, field, value) => {
    const updated = [...newUsers];
    updated[index] = { ...updated[index], [field]: value };
    setNewUsers(updated);
    onChange({ ...data, new_users: updated });
  };

  const removeUser = (index) => {
    const updated = newUsers.filter((_, i) => i !== index);
    setNewUsers(updated);
    onChange({ ...data, new_users: updated });
  };

  const generatePassword = (index) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    updateUser(index, 'temp_password', password);
  };

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={Users} 
        title="Équipe et accès"
        description="Créez les comptes utilisateurs pour votre équipe. Chaque personne aura un accès personnalisé selon son rôle. Vous pourrez aussi gérer les utilisateurs depuis les paramètres."
      />

      {existingUsers.length > 0 && (
        <div>
          <h5 className="font-medium text-gray-900 mb-3">Utilisateurs existants ({existingUsers.length})</h5>
          <div className="space-y-2">
            {existingUsers.map((u, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-semibold">
                  {(u.first_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{u.first_name} {u.last_name}</p>
                  <p className="text-sm text-gray-500">{u.email} • {u.role}</p>
                </div>
                {u.is_owner && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">Propriétaire</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h5 className="font-medium text-gray-900">Nouveaux utilisateurs</h5>
        
        {newUsers.map((user, index) => (
          <div key={index} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex justify-between items-center mb-3">
              <h6 className="font-medium text-gray-700">Utilisateur {index + 1}</h6>
              <button type="button" onClick={() => removeUser(index)} className="text-gray-400 hover:text-red-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input type="email" value={user.email} onChange={(e) => updateUser(index, 'email', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg" placeholder="Email *" data-testid={`user-email-${index}`} />
              <input type="text" value={user.first_name} onChange={(e) => updateUser(index, 'first_name', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg" placeholder="Prénom" />
              <input type="text" value={user.last_name} onChange={(e) => updateUser(index, 'last_name', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg" placeholder="Nom" />
              <select value={user.role} onChange={(e) => updateUser(index, 'role', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg">
                {roles.map(r => <option key={r.code} value={r.code}>{r.label} - {r.description}</option>)}
              </select>
              <div className="md:col-span-2 flex gap-2">
                <input type="text" value={user.temp_password} onChange={(e) => updateUser(index, 'temp_password', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg" placeholder="Mot de passe temporaire" />
                <button type="button" onClick={() => generatePassword(index)}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm whitespace-nowrap">
                  Générer
                </button>
              </div>
            </div>
          </div>
        ))}

        <button type="button" onClick={addUser}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-amber-500 hover:text-amber-600 flex items-center justify-center gap-2"
          data-testid="add-user-button">
          <Plus className="w-5 h-5" /> Ajouter un utilisateur
        </button>
      </div>
    </div>
  );
};

// ============ STEP 11: MODULES ============
const StepModules = ({ data, onChange }) => {
  const modules = data.modules || {};
  const available = [
    { code: 'reservations', label: 'Réservations', icon: '📅', required: true, description: 'Gestion des réservations et calendrier' },
    { code: 'calendar', label: 'Calendrier', icon: '📆', description: 'Vue calendrier des disponibilités' },
    { code: 'precheckin', label: 'Pré-check-in', icon: '📝', description: 'Formulaire en ligne pour les clients' },
    { code: 'guests', label: 'Clients', icon: '👥', description: 'Base de données clients' },
    { code: 'pos', label: 'Point de vente (POS)', icon: '🛒', description: 'Facturation de services sur place' },
    { code: 'payments', label: 'Paiements', icon: '💳', description: 'Suivi des encaissements' },
    { code: 'expenses', label: 'Dépenses', icon: '🧾', description: 'Gestion des dépenses' },
    { code: 'charges', label: 'Charges', icon: '📊', description: 'Charges fixes et récurrentes' },
    { code: 'payouts', label: 'Virements OTA', icon: '🏦', description: 'Suivi des virements plateformes' },
    { code: 'hr', label: 'RH', icon: '👔', description: 'Gestion du personnel' },
    { code: 'reports', label: 'Rapports', icon: '📈', description: 'Tableaux de bord et statistiques' },
    { code: 'backup', label: 'Sauvegarde', icon: '💾', description: 'Sauvegarde automatique des données' }
  ];

  const toggle = (code) => {
    const mod = available.find(m => m.code === code);
    if (mod?.required) return;
    // Toggle: if currently enabled (true or undefined), set to false; if false, set to true
    const currentlyEnabled = modules[code] !== false;
    onChange({ ...data, modules: { ...modules, [code]: !currentlyEnabled } });
  };

  // CORRECTION BUG: Comptage cohérent avec l'affichage
  // Un module est "enabled" si: required OU (non défini dans modules) OU (défini comme true)
  // Soit: required OU (modules[code] !== false)
  const enabledCount = available.filter(m => m.required || modules[m.code] !== false).length;

  return (
    <div className="space-y-6">
      <StepInfoBox 
        icon={Grid3X3} 
        title="Modules de l'application"
        description="Activez uniquement les modules dont vous avez besoin. Cela simplifie l'interface et vous permet de vous concentrer sur l'essentiel. Vous pourrez activer d'autres modules plus tard."
      />

      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => {
          const all = {};
          available.forEach(m => { all[m.code] = true; });
          onChange({ ...data, modules: all });
        }} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">Tout activer</button>
        <button type="button" onClick={() => {
          const min = {};
          available.forEach(m => { min[m.code] = m.required || false; });
          onChange({ ...data, modules: min });
        }} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">Minimum</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {available.map(m => {
          const enabled = m.required || modules[m.code] !== false;
          return (
            <button key={m.code} type="button" onClick={() => toggle(m.code)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${enabled ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
              data-testid={`module-${m.code}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl">{m.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-medium ${enabled ? 'text-gray-900' : 'text-gray-400'}`}>{m.label}</p>
                    {m.required && <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">Requis</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{m.description}</p>
                </div>
                <div className={`w-5 h-5 rounded-full flex-shrink-0 ${enabled ? 'bg-green-500' : 'bg-gray-200'}`}>
                  {enabled && <CheckCircle2 className="w-5 h-5 text-white" />}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-center text-sm text-gray-500">
        <span className="font-semibold text-green-600">{enabledCount}</span> modules activés sur {available.length}
      </p>
    </div>
  );
};

// ============ STEP 12: CHECKLIST / SUMMARY ============
const StepChecklist = ({ data }) => {
  const { riad, summary, checklist, ready } = data;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${ready ? 'bg-green-100' : 'bg-amber-100'}`}>
          <CheckCircle2 className={`w-10 h-10 ${ready ? 'text-green-600' : 'text-amber-600'}`} />
        </div>
        <h3 className="text-2xl font-bold text-gray-900">{ready ? 'Tout est prêt !' : 'Presque terminé'}</h3>
        <p className="text-gray-500">{riad?.name}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h5 className="font-semibold text-gray-900 mb-4">Vérification de la configuration</h5>
        <div className="space-y-2">
          {(checklist || []).map((item, i) => (
            <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${item.done ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-green-500' : 'bg-gray-300'}`}>
                {item.done && <CheckCircle2 className="w-4 h-4 text-white" />}
              </div>
              <span className={item.done ? 'text-gray-900' : 'text-gray-500'}>{item.item}</span>
            </div>
          ))}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Capacité max', value: `${summary.capacity?.max_guests || 0} PAX` },
            { label: 'Services', value: summary.products_count || 0 },
            { label: 'Canaux', value: summary.channels?.length || 0 },
            { label: 'Utilisateurs', value: summary.users_count || 0 }
          ].map((s, i) => (
            <div key={i} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 text-center border border-amber-200">
              <p className="text-2xl font-bold text-amber-600">{s.value}</p>
              <p className="text-sm text-gray-600">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-800">
          <strong>Prochaine étape :</strong> Après validation, vous serez redirigé vers la page des paramètres pour finaliser la configuration et commencer à utiliser l'application.
        </p>
      </div>
    </div>
  );
};

// ============ MAIN WIZARD ============
const STEPS = [
  { id: 'RIAD_INFO', label: 'Établissement', icon: Building2, component: StepRiadInfo },
  { id: 'FLUX_FINANCIERS', label: 'Flux financiers', icon: Landmark, component: StepFluxFinanciers },
  { id: 'TAXES', label: 'Fiscalité', icon: Receipt, component: StepTaxes },
  { id: 'PRODUCTS', label: 'Services', icon: ShoppingBag, component: StepProducts },
  { id: 'CATEGORIES', label: 'Catégories', icon: FolderTree, component: StepCategories },
  { id: 'CHANNELS', label: 'Canaux OTA', icon: Globe, component: StepChannels },
  { id: 'PRECHECKIN', label: 'Pré-check-in', icon: ClipboardCheck, component: StepPrecheckin },
  { id: 'CALENDAR', label: 'Calendrier', icon: Calendar, component: StepCalendar },
  { id: 'DOCUMENTS', label: 'Documents', icon: FileText, component: StepDocuments },
  { id: 'USERS', label: 'Équipe', icon: Users, component: StepUsers },
  { id: 'MODULES', label: 'Modules', icon: Grid3X3, component: StepModules },
  { id: 'CHECKLIST', label: 'Validation', icon: CheckCircle2, component: StepChecklist }
];

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepData, setStepData] = useState({});
  const [setupStatus, setSetupStatus] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  
  const currentStep = STEPS[currentStepIndex];

  useEffect(() => {
    loadSetupStatus();
  }, []);

  const loadSetupStatus = async () => {
    try {
      const response = await api.get('/setup/status');
      setSetupStatus(response.data);
      
      if (response.data.setup_state === 'COMPLETED') {
        navigate('/settings');
        return;
      }
      
      const stepIndex = STEPS.findIndex(s => s.id === response.data.current_step);
      if (stepIndex >= 0) {
        setCurrentStepIndex(stepIndex);
      }
      
      await loadStepData(STEPS[Math.max(0, stepIndex)].id);
    } catch (error) {
      console.error('Error loading setup status:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadStepData = async (stepId) => {
    try {
      const response = await api.get(`/setup/step/${stepId}`);
      setStepData(prev => ({ ...prev, [stepId]: response.data }));
    } catch (error) {
      console.error('Error loading step data:', error);
    }
  };

  const saveCurrentStep = async () => {
    if (currentStep.id === 'CHECKLIST') return true;
    
    setSaving(true);
    try {
      const data = stepData[currentStep.id] || {};
      await api.post(`/setup/step/${currentStep.id}`, data);
      return true;
    } catch (error) {
      console.error('Error saving step:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la sauvegarde');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const saved = await saveCurrentStep();
    if (!saved) return;
    
    if (currentStepIndex < STEPS.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      await loadStepData(STEPS[nextIndex].id);
      toast.success('Sauvegardé');
    }
  };

  const handlePrev = async () => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      if (!stepData[STEPS[prevIndex].id]) {
        await loadStepData(STEPS[prevIndex].id);
      }
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await api.post('/setup/complete');
      toast.success('Configuration terminée !');
      await refreshProfile();
      // Show welcome screen instead of redirecting
      setShowWelcome(true);
    } catch (error) {
      console.error('Error completing setup:', error);
      toast.error('Erreur lors de la finalisation');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    if (!window.confirm('Les valeurs par défaut seront appliquées. Vous pourrez modifier ces paramètres plus tard dans les réglages. Continuer ?')) return;
    
    setSaving(true);
    try {
      await api.post('/setup/skip');
      toast.success('Configuration terminée avec les valeurs par défaut');
      await refreshProfile();
      // Show welcome screen
      setShowWelcome(true);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const goToStep = async (index) => {
    if (index <= currentStepIndex) {
      setCurrentStepIndex(index);
      if (!stepData[STEPS[index].id]) {
        await loadStepData(STEPS[index].id);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
      </div>
    );
  }

  // Show welcome screen after completion
  if (showWelcome) {
    return (
      <OnboardingWelcome 
        riadName={setupStatus?.riad_name} 
        onDismiss={() => navigate('/dashboard')}
      />
    );
  }

  const StepComponent = currentStep.component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100" data-testid="setup-wizard-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h1 className="text-xl font-bold text-gray-900">Configuration de votre établissement</h1>
          </div>
          <p className="text-sm text-gray-600">{setupStatus?.riad_name}</p>
        </div>
        
        {/* Progress - Compact */}
        <div className="mb-6 overflow-x-auto pb-2">
          <div className="flex items-center justify-between min-w-max px-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;
              
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => goToStep(index)}
                    disabled={index > currentStepIndex}
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-xs transition-all
                      ${isCompleted ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600' : ''}
                      ${isCurrent ? 'bg-amber-500 text-white ring-2 ring-amber-300' : ''}
                      ${!isCompleted && !isCurrent ? 'bg-gray-200 text-gray-400' : ''}
                    `}
                    title={step.label}
                  >
                    {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </button>
                  {index < STEPS.length - 1 && (
                    <div className={`w-4 md:w-6 h-0.5 mx-0.5 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-gray-500 mt-2">
            Étape {currentStepIndex + 1}/{STEPS.length} - <span className="font-medium">{currentStep.label}</span>
          </p>
        </div>
        
        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-5 md:p-6 mb-6">
          <StepComponent 
            data={stepData[currentStep.id] || {}}
            onChange={(newData) => setStepData(prev => ({ ...prev, [currentStep.id]: newData }))}
          />
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStepIndex === 0 || saving}
            className="flex items-center gap-1 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
            data-testid="prev-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Précédent</span>
          </button>
          
          <button type="button" onClick={handleSkip} disabled={saving} className="text-xs text-gray-400 hover:text-gray-600">
            Passer et utiliser les valeurs par défaut
          </button>
          
          {currentStep.id === 'CHECKLIST' ? (
            <button
              type="button"
              onClick={handleComplete}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
              data-testid="complete-button"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              Terminer et accéder aux réglages
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50"
              data-testid="next-button"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <span className="hidden sm:inline">Suivant</span>
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
